import React, { useState, useEffect, useCallback } from 'react';
import { getGalleryImages } from '../lib/cms';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Gallery({ pageSlug, title = "Se vores arbejde" }) {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);

  useEffect(() => {
    async function loadGallery() {
      const galleryImages = await getGalleryImages(pageSlug);
      setImages(galleryImages);
    }
    loadGallery();
  }, [pageSlug]);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setCurrentIndex(null);
    document.body.style.overflow = '';
  }, []);

  const showPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
  }, [images.length]);

  const showNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (currentIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, closeLightbox, showPrev, showNext]);

  if (images.length === 0) return null;

  return (
    <section className="py-16 bg-neutral-surface-light border-t border-border-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-text-primary tracking-tight mb-8 text-center">
          {title}
        </h2>

        {/* Image Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {images.map((img, index) => {
            const imageUrl = Array.isArray(img.image) ? img.image[0] : img.image;
            return (
              <div
                key={img.id || index}
                onClick={() => openLightbox(index)}
                className="group relative aspect-4/3 bg-slate-200 rounded-none overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer border border-border-light"
              >
                <img
                  src={imageUrl}
                  alt={img.alt || "Skals Vognmandsforretning - Vognmandskørsel, kran og lastbiler"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox Modal */}
      {currentIndex !== null && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 bg-brand-dark/95 z-50 flex flex-col items-center justify-center p-4 animate-in fade-in duration-200"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 bg-brand-dark/80 hover:bg-brand-red text-white p-3 rounded-none transition-colors z-50 focus:outline-none cursor-pointer"
            aria-label="Luk galleri"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Image Container */}
          <div className="relative max-w-5xl w-full max-h-[80vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {/* Left Arrow */}
            <button
              onClick={showPrev}
              className="absolute left-2 sm:left-4 bg-brand-dark/80 hover:bg-brand-red text-white p-3 rounded-none transition-colors z-50 focus:outline-none cursor-pointer"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            {/* Main Image */}
            <img
              src={Array.isArray(images[currentIndex].image) ? images[currentIndex].image[0] : images[currentIndex].image}
              alt={images[currentIndex].alt || "Skals Vognmandsforretning - Vognmandskørsel, kran og lastbiler"}
              className="max-w-full max-h-[80vh] object-contain rounded-none shadow-2xl select-none border border-border-light"
            />

            {/* Right Arrow */}
            <button
              onClick={showNext}
              className="absolute right-2 sm:right-4 bg-brand-dark/80 hover:bg-brand-red text-white p-3 rounded-none transition-colors z-50 focus:outline-none cursor-pointer"
              aria-label="Næste billede"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          {/* Caption & Counter */}
          <div className="mt-6 text-center max-w-2xl px-4" onClick={(e) => e.stopPropagation()}>
            {images[currentIndex].alt && (
              <p className="text-white text-base sm:text-lg font-semibold leading-relaxed mb-2">
                {images[currentIndex].alt}
              </p>
            )}
            <p className="text-slate-400 text-xs sm:text-sm tracking-wider uppercase">
              Billede {currentIndex + 1} af {images.length}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
